import React, { useEffect } from 'react'
import { Map } from 'immutable'
import {
  createSystemNotification,
  deleteSystemNotification,
} from 'actions/application'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'hooks/redux'
import { useDispatch } from 'react-redux'
import {
  ACTIVE,
  NOTIFICATION_INFO,
  OBSOLETE,
  restrictedModules,
} from 'utils/constants'
import strings from 'utils/strings'
import { hasRight, getIsUserInternal } from 'selectors/authorization'
import {
  VIEW_FEATURE_REVISION,
  CREATE_FEATURE_REVISION,
} from 'constants/authorization'

export const FEATURE_REIVISION_NOTIFICATION_ID = 'feature-revision-notification'
export const FEATURE_LATEST_REVISION_NOTIFICATION_ID =
  'feature-latest-revision-notification'
export const FEATURE_INACTIVE_LATEST_NOTIFICATION_ID =
  'feature-inactive-latest-notification'
export const FEATURE_ACTIVE_PREVIOUS_NOTIFICATION_ID =
  'feature-active-previous-notification'
const DEFAULT_FEATURE_REVISION = '-'

function useCurrentFeatureId(featureId) {
  const currentFeatureId = featureId
  return currentFeatureId
}

function getFeatureRevision(state, featureId) {
  // This function retrieves the feature revision from the state based on the featureId.
  // It currently returns a placeholder value, but can be extended to fetch actual data.
  return state.getIn(
    ['feature', 'currentFeature', 'feature', featureId, 'revision'],
    DEFAULT_FEATURE_REVISION
  )
}

function getFeatureRevisions(state, featureId) {
  // This function retrieves all feature revisions for a given featureId.
  // It currently returns an empty array, but can be extended to fetch actual data.
  // console.log(state.getIn(['feature', 'currentFeature', 'revisions']))
  return (
    state
      .getIn(
        ['feature', 'currentFeature', 'feature', featureId, 'revisions'],
        new Map()
      )
      ?.valueSeq()
      .toJS() || []
  )
}

function getCurrentFeatureUri(state, featureId) {
  return state.getIn(
    ['feature', 'currentFeature', 'feature', featureId, 'uri'],
    null
  )
}

export function useFeatureRevision(featureId) {
  // This hook is currently a placeholder.
  // It can be extended in the future to include logic related to feature revisions.
  const currentFeatureId = useCurrentFeatureId(featureId)
  const featureRevision = useSelector(getFeatureRevision, currentFeatureId)

  return featureRevision
}

export function useFeatureRevisions(featureId) {
  // This hook retrieves all feature revisions for the current feature.
  const currentFeatureId = useCurrentFeatureId(featureId)
  const featureRevisions = useSelector(getFeatureRevisions, currentFeatureId)
  return featureRevisions
}

export function useIsLatestRevision(featureId) {
  const currentFeatureUri = useSelector(getCurrentFeatureUri, featureId)
  const revisions = useFeatureRevisions(featureId)

  // Edge case: no featureId or no revisions
  if (!featureId || !revisions || revisions.length === 0) {
    return true
  }

  // Edge case: only one revision exists
  if (revisions.length === 1) {
    return true
  }

  // Compare current feature URI with latest revision URI
  const latestRevision = revisions[revisions.length - 1]
  const latestRevisionUri = latestRevision?.uri

  return currentFeatureUri === latestRevisionUri
}

export function useIsSecondNewestRevision(featureId) {
  const currentFeatureUri = useSelector(getCurrentFeatureUri, featureId)
  const revisions = useFeatureRevisions(featureId)

  // Need at least 2 revisions
  if (!featureId || !revisions || revisions.length < 2) {
    return false
  }

  const secondNewestRevision = revisions[revisions.length - 2]
  const secondNewestUri = secondNewestRevision?.uri

  return currentFeatureUri === secondNewestUri
}

export function useFeatureRevisionNotification(featureId) {
  const dispatch = useDispatch()
  const history = useHistory()
  const [notified, setNotified] = React.useState(false)
  const revisions = useFeatureRevisions(featureId)
  const isFeatureLatestRevision = useIsLatestRevision(featureId)
  const isSecondNewestRevision = useIsSecondNewestRevision(featureId)
  const canViewRevision = useSelector(hasRight, VIEW_FEATURE_REVISION)

  const createFeatureRevisionNotification = React.useCallback(() => {
    // Only show notifications if user has the VIEW_FEATURE_REVISION right
    if (canViewRevision !== true) {
      return
    }

    // NEW: Handle case where newest revision is NOT Active
    // Show notification on newest revision to navigate to Active (second-newest)
    const newestRevision = revisions[revisions.length - 1]
    const secondNewestRevision = revisions[revisions.length - 2]

    if (
      featureId &&
      revisions.length >= 2 &&
      isFeatureLatestRevision === true &&
      newestRevision?.status !== ACTIVE &&
      secondNewestRevision?.status === ACTIVE &&
      !notified
    ) {
      setNotified(true)
      const actionTrigger = {
        onClick: () => {
          history.push(secondNewestRevision.uri)
          window.location.reload()
        },
        text: strings.revision_go_second_newest,
        onColor: 'dark',
      }
      dispatch(
        createSystemNotification({
          notificationMessage: strings.is_most_recent_version,
          actionTrigger,
          notificationType: NOTIFICATION_INFO,
          notificationId: FEATURE_INACTIVE_LATEST_NOTIFICATION_ID,
          persistent: true,
          hideOnPaths: [/^(?!\/features)/],
        })
      )
    }

    // NEW: Handle case on Active second-newest revision
    // Show notification to navigate to newest revision
    if (
      featureId &&
      revisions.length >= 2 &&
      isSecondNewestRevision === true &&
      newestRevision?.status !== ACTIVE &&
      secondNewestRevision?.status === ACTIVE &&
      !notified
    ) {
      setNotified(true)
      const actionTrigger = {
        onClick: () => {
          history.push(newestRevision.uri)
          window.location.reload()
        },
        text: strings.revision_go_newest,
        onColor: 'dark',
      }
      dispatch(
        createSystemNotification({
          notificationMessage: strings.active_revision_has_newer_version,
          actionTrigger,
          notificationType: NOTIFICATION_INFO,
          notificationId: FEATURE_ACTIVE_PREVIOUS_NOTIFICATION_ID,
          persistent: true,
          hideOnPaths: [/^(?!\/features)/],
        })
      )
    }

    // Handle "IS latest revision" case
    if (
      featureId &&
      isFeatureLatestRevision === true &&
      !notified &&
      newestRevision?.status === ACTIVE &&
      secondNewestRevision?.status !== OBSOLETE &&
      revisions.length > 1 // Only show if there are multiple revisions
    ) {
      setNotified(true)
      dispatch(
        createSystemNotification({
          notificationMessage: strings.is_most_recent_version,
          notificationType: NOTIFICATION_INFO,
          notificationId: FEATURE_LATEST_REVISION_NOTIFICATION_ID,
          persistent: true,
          hideOnPaths: [/^(?!\/features)/],
        })
      )
    }
  }, [
    dispatch,
    isFeatureLatestRevision,
    isSecondNewestRevision,
    notified,
    featureId,
    revisions,
    history,
    canViewRevision,
  ])

  const deleteFeatureRevisionNotification = React.useCallback(() => {
    dispatch(
      deleteSystemNotification({
        notificationId: FEATURE_REIVISION_NOTIFICATION_ID,
      })
    )
    dispatch(
      deleteSystemNotification({
        notificationId: FEATURE_LATEST_REVISION_NOTIFICATION_ID,
      })
    )
    dispatch(
      deleteSystemNotification({
        notificationId: FEATURE_INACTIVE_LATEST_NOTIFICATION_ID,
      })
    )
    dispatch(
      deleteSystemNotification({
        notificationId: FEATURE_ACTIVE_PREVIOUS_NOTIFICATION_ID,
      })
    )
  }, [dispatch])

  useEffect(() => {
    createFeatureRevisionNotification()
    // return () => {
    //   deleteFeatureRevisionNotification()
    // }
  }, [createFeatureRevisionNotification, deleteFeatureRevisionNotification])

  // Reset notified flag when featureId changes
  useEffect(() => {
    setNotified(false)
    return () => {
      deleteFeatureRevisionNotification()
    }
  }, [featureId, deleteFeatureRevisionNotification])

  return {
    createFeatureRevisionNotification,
    deleteFeatureRevisionNotification,
  }
}

export function useFeatureCanCreateRevision(featureId) {
  // This hook checks if the current feature can create a revision.
  // It currently returns true, but can be extended to include actual logic.
  const isFeatureActive =
    useSelector((state) =>
      state.getIn(
        ['feature', 'currentFeature', 'feature', featureId, 'status'],
        false
      )
    ) === ACTIVE
  const isLatestRevision = useIsLatestRevision(featureId)
  return isFeatureActive && isLatestRevision
}

/**
 * Returns true if the current user has the right to create a revision of
 * the given feature, based on the feature's module type.
 *
 * Business rules:
 *   - Internal modules (ASAM, XMC1, XMC2): requires create_feature_revision_internal_modules
 *   - Custom modules  (XMC3–XMC8):         requires create_feature_revision_custom_modules
 *
 * The internal role check is a temporary frontend guard while the backend
 * incorrectly grants these rights to non-internal users.
 */
export function useHasCreateRevisionRight(featureId) {
  const isInternal = useSelector(getIsUserInternal)
  return useSelector((state) => {
    if (!isInternal) return false
    const moduleName = state.getIn(
      ['feature', 'currentFeature', 'feature', featureId, 'moduleName'],
      null
    )
    const isInternalModule = restrictedModules.includes(moduleName)
    const requiredRight = isInternalModule
      ? CREATE_FEATURE_REVISION.INTERNAL_MODULES
      : CREATE_FEATURE_REVISION.CUSTOM_MODULES
    return hasRight(state, requiredRight)
  })
}
